import { SIPDeviceCreationForm } from './SIPDeviceCreationForm.container';
import { SIPDeviceRow } from './SIPDeviceRow.container';

import {
  Button,
  Icon,
  PlusCircleFilled,
  RefreshOutlined,
  Spacer,
  Typography,
  useTheme,
} from '@aircall/tractor';
import { AddButton } from '@components/Button/AddButton';
import { List } from '@components/List/List';
import { Paper } from '@components/Paper/Paper';
import { useSIPDeviceCreation } from '@hooks/useSIPDeviceCreation';
import { useToggle } from '@hooks/useToggle/useToggle';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { useTranslation } from 'react-i18next';

export interface ISIPDeviceCreationForm {
  domain: string;
  username: string;
}

export function SIPDevicesSection({ user }: UserProfileTabProps) {
  const { t } = useTranslation();
  const { space } = useTheme();

  const [isCreationModalOpen, _, openCreationModal, closeCreationModal] = useToggle(false);

  const {
    onSIPDeviceSubmit,
    loading: submittingSIPDevice,
    customSipSettings = [],
    twilioSipSettings = [],
  } = useSIPDeviceCreation({ user });

  return (
    <Paper
      title={t('user_profile.call_preferences.forward_call_sip.title')}
      subtitle={t('user_profile.call_preferences.forward_call_sip.caption')}
    >
      <Spacer fluid space='xxs' direction='vertical' mb='m'>
        <Typography>{t('user_profile.call_preferences.forward_call_sip.sip_devices')}</Typography>
        {customSipSettings.length && (
          <List>
            {customSipSettings.map((sipSetting) => (
              <SIPDeviceRow key={sipSetting.ID} sipSetting={sipSetting} />
            ))}
          </List>
        )}
        <AddButton
          hasData={!!customSipSettings.length}
          data-test='user-profile-call-preferences-add-sip-credentials'
          translationKey='user_profile.call_preferences.forward_call_sip.add_credentials'
          onClick={openCreationModal}
        />
      </Spacer>
      <Spacer fluid space='xxs' direction='vertical'>
        <Typography>
          {t('user_profile.call_preferences.forward_call_sip.generate_credentials')}
        </Typography>
        {twilioSipSettings.length ? (
          <>
            <List>
              {twilioSipSettings.map((sipSetting) => (
                <SIPDeviceRow key={sipSetting.ID} sipSetting={sipSetting} />
              ))}
            </List>

            <Button
              mode='link'
              onClick={onSIPDeviceSubmit}
              disabled={submittingSIPDevice}
              data-test='regenerate-button'
            >
              <Icon component={RefreshOutlined} size={space.s} />
              {t('user_profile.call_preferences.forward_call_sip.regenerate_credentials')}
            </Button>
          </>
        ) : (
          <Button
            mode='outline'
            onClick={onSIPDeviceSubmit}
            disabled={submittingSIPDevice}
            data-test='generate-button'
          >
            <PlusCircleFilled />
            {t('user_profile.call_preferences.forward_call_sip.generate_credentials')}
          </Button>
        )}
      </Spacer>
      <SIPDeviceCreationForm onClose={closeCreationModal} show={isCreationModalOpen} />
    </Paper>
  );
}
